import { createRef, Component, JSX } from "react";
import Portal from "@app/shared/Portal";

import { removeLocalStorageRunConfigurationIds } from "@app/velvet/helpers";
import { QESButton } from "@app/shared/qes-standards/Button";
import AuthenticationService from "@app/authentication/authenticationService";

interface InactivityMonitorState {
  cancelSessionTimeout: boolean;
  showInactivityWarningModal: boolean;
}

class InactivityMonitor extends Component<unknown, InactivityMonitorState> {
  private readonly defaultCallbackMilliseconds = ((window.sessionIdleMinutes * 60) - window.sessionIdleWarningSeconds) * 1000;
  private readonly secondsToWarnInactive = ((window.sessionIdleMinutes * 60) - window.sessionIdleWarningSeconds);
  private countdown = window.sessionIdleWarningSeconds;
  private countdownRef = createRef<HTMLSpanElement>();
  private continueSessionButtonRef = createRef<HTMLButtonElement>();

  constructor(props: unknown) {
    super(props);

    this.state = {
      cancelSessionTimeout: false,
      showInactivityWarningModal: false,
    };
  }

  public componentDidMount(): void {
    window.addEventListener("click", this.handleUserActivity);
    window.addEventListener("contextmenu", this.handleUserActivity);
    window.addEventListener("scroll", this.handleUserActivity);
    window.addEventListener("resize", this.handleUserActivity);
    window.addEventListener("keydown", this.handleUserActivity);

    this.LastActiveTime = Date.now();
    this.scheduleCallback();
  }

  public componentWillUnmount(): void {
    window.removeEventListener("click", this.handleUserActivity);
    window.removeEventListener("contextmenu", this.handleUserActivity);
    window.removeEventListener("scroll", this.handleUserActivity);
    window.removeEventListener("resize", this.handleUserActivity);
    window.removeEventListener("keydown", this.handleUserActivity);
  }

  public render(): JSX.Element {
    return !this.state.showInactivityWarningModal
      ? <></>
      : (
        <Portal hideBodyScroll={true}>
          <div className={`modal show-modal modal-height-275 modal-width-415`}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-body pt10 mt6">
                  <strong>Your session is about to expire.</strong><br />You will be logged out in <span ref={this.countdownRef}>{this.countdown}</span> seconds
                </div>
                <div className="modal-footer">
                  <QESButton qesTestId="inactivity-monitor_continue-session" onClick={this.handleContinueSession} qesRef={this.continueSessionButtonRef}>Continue Session</QESButton>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop" />
        </Portal>
      );
  }

  private get LastActiveTime(): number {
    let lastActivity: string | number = localStorage.getItem("lastActivity");
    if (!lastActivity) {
      lastActivity = Date.now();
      this.LastActiveTime = lastActivity;
    }
    return +lastActivity;
  }

  private set LastActiveTime(lastActivity: number) {
    localStorage.setItem("lastActivity", lastActivity.toString());
  }

  private checkForInactivity = () => {
    if (!this.state.showInactivityWarningModal) {
      const secondsUntilWarningShouldShow = this.getSecondsUntilWarning();

      // if user has interacted with the page recently enough to not warrant a inactivity timing out warning
      if (secondsUntilWarningShouldShow > 0) {
        this.scheduleCallback(secondsUntilWarningShouldShow * 1000);
        return;
      }

      // it's now time to warn the user is that they're about to be considered inactive
      this.setState((prevState) => ({
        ...prevState,
        showInactivityWarningModal: true,
      }), () => this.handleCountdown());
    }
  }

  private getSecondsUntilWarning() {
    const now = Date.now();
    const secondsOfInactivity = Math.floor(Math.abs(now - this.LastActiveTime) / 1000);
    return this.secondsToWarnInactive - secondsOfInactivity;
  }

  private handleContinueSession = () => {
    this.countdown = window.sessionIdleWarningSeconds;

    this.setState((prevState) => ({
      ...prevState,
      cancelSessionTimeout: true,
      showInactivityWarningModal: false,
    }), this.scheduleCallback);
  }

  private handleCountdown = async () => {
    if (!this.state.cancelSessionTimeout) {
      const countdown = this.countdown - 1;
      this.countdown = countdown;
      this.countdownRef.current.innerHTML = countdown.toString();

      if (countdown > 0) {
        setTimeout(() => { this.handleCountdown(); }, 1000);
        return;
      }

      // this looks odd that we're checking if the warning should show even though, at this point, the warning is already
      // shown and the countdown has expired, but what we're actually doing is leveraging the nature of localStorage to ensure
      // that an inactive tab in the background doesn't end the session when a user has clicked "continue session" in another tab
      this.continueSessionButtonRef.current.disabled = true;
      const secondsUntilWarningShouldShow = this.getSecondsUntilWarning();

      if (secondsUntilWarningShouldShow <= 0) {
        removeLocalStorageRunConfigurationIds();
        await AuthenticationService.getInstance().logout("InactivityTimeout");
      } else {
        this.countdown = window.sessionIdleWarningSeconds;

        this.setState((prevState) => ({
          ...prevState,
          showInactivityWarningModal: false,
        }), () => this.scheduleCallback(secondsUntilWarningShouldShow));
      }

    } else {
      this.setState((prevState) => ({
        ...prevState,
        cancelSessionTimeout: false,
      }));
    }

  }

  private handleUserActivity = () => {
    // reset latest time of recorded activity
    const now = Date.now();
    this.LastActiveTime = now;
  }

  private scheduleCallback = (callbackMilliseconds?: number) => {
    callbackMilliseconds = callbackMilliseconds !== undefined
      ? callbackMilliseconds
      : this.defaultCallbackMilliseconds;

    setTimeout(() => {
      this.checkForInactivity();
    }, callbackMilliseconds);
  }
}

export {
  InactivityMonitor,
};
